import { Microscope, Dna, FlaskConical, TrendingUp, Droplets, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface OnboardingSlideProps {
  onComplete: () => void;
  isCompleting: boolean;
  isLastSlide: boolean;
  onNext?: () => void;
  onGenderSelect?: (gender: string) => void;
}
export const OnboardingSlide2 = ({ onNext }: OnboardingSlideProps) => {
  return <Card className="shadow-card border-0 bg-gradient-card">
    <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center">
      {/* Visual */}
      <div className="mb-4 relative">
        <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-primary flex items-center justify-center animate-scale-in shadow-glow">
          <Microscope className="w-12 h-12 md:w-14 md:h-14 text-primary-foreground" />
        </div>
      </div>

      {/* Title */}
      <h2 className="text-xl md:text-2xl font-semibold mb-3 animate-fade-in">
        Built on Skin Science
      </h2>

      {/* Subtext */}
      <p className="text-sm text-muted-foreground max-w-2xl mb-4 animate-fade-in" style={{
      animationDelay: '0.2s'
    }}>
        FaceFable analyses your skin using advanced AI and dermatology-based metrics to create your unique FaceScore — helping you understand, track, and improve your skin over time.
      </p>

      {/* Key Feature Icons */}
      <div className="mt-4 w-full max-w-2xl space-y-2 animate-fade-in" style={{
      animationDelay: '0.4s'
    }}>
        {/* Feature 1 */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold mb-0">AI-Powered Analysis</p>
            <p className="text-xs text-muted-foreground">Detects hydration, tone, and texture through your camera</p>
          </div>
        </div>

        {/* Feature 2 */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Dna className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold mb-0">Dermatology Metrics</p>
            <p className="text-xs text-muted-foreground">Built with clinical parameters for accuracy</p>
          </div>
        </div>

        {/* Feature 3 */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <FlaskConical className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold mb-0">Ingredient Intelligence</p>
            <p className="text-xs text-muted-foreground">Analyses your skincare ingredients to reveal what works (and what doesn&apos;t)</p>
          </div>
        </div>

        {/* Feature 4 */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold mb-0">Your FaceScore</p>
            <p className="text-xs text-muted-foreground">A live score showing real progress</p>
          </div>
        </div>

        {/* Feature 5 */}
        <div className="flex items-start gap-2 p-2 rounded-lg bg-muted/30 border border-border/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Droplets className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold mb-0">Real Results</p>
            <p className="text-xs text-muted-foreground">See your glow improve, one scan at a time</p>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <Button 
        size="lg" 
        className="w-full max-w-md mt-6 bg-gradient-primary hover:opacity-90 shadow-button"
        onClick={onNext}
      >
        Continue
      </Button>
    </CardContent>
  </Card>;
};